import type { Events, States } from '../common/fsm.js'
import type NavItem from '../nav-item/NavItem.js'
import * as lockIcon from '@nordhealth/icons/lib/assets/navigation-toggle-lock.js'
import { html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { styleMap } from 'lit/directives/style-map.js'
import { BroadcastChannelController } from '../common/controllers/BroadcastChannelController.js'
import { DirectionController } from '../common/controllers/DirectionController.js'
import { EventController } from '../common/controllers/EventController.js'
import { LightDismissController } from '../common/controllers/LightDismissController.js'
import { SlotController } from '../common/controllers/SlotController.js'
import { observe } from '../common/decorators/observe.js'
import { cond } from '../common/directives/cond.js'
import { fsm } from '../common/fsm.js'
import { clamp } from '../common/number.js'
import { storage } from '../common/storage.js'

import componentStyle from '../common/styles/Component.css'
import stickyStyle from '../common/styles/Sticky.css'

import Icon from '../icon/Icon.js'
import style from './Layout.css'
import '../nav-toggle/NavToggle.js'

Icon.registerIcon(lockIcon)

const NAV_DEFAULT_WIDTH = 250
const NAV_MIN_WIDTH = 220
const NAV_MAX_WIDTH = 400
const NAV_COLLAPSE_WIDTH = 100
const NAV_RESIZE_STEP = 30
const NAV_PEEK_DELAY = 300

const mediaQuery
  = typeof matchMedia !== 'undefined'
    ? matchMedia('(min-width: 768px)')
    : ({
        matches: false,
        addEventListener() {
          /* noop */
        },
      } as unknown as MediaQueryList)

const navWidthStore = storage('nord-layout.navWidth', NAV_DEFAULT_WIDTH)
const navOpenStore = storage('nord-layout.navOpen', true)

const navMachine = fsm({
  opened: {
    toggle: 'closed',
    close: 'closed',
  },
  closed: {
    toggle: 'opened',
    open: 'opened',
    focusin: 'peek',
    pointerenter: 'peek',
  },
  peek: {
    toggle: 'opened',
    focusout: 'unpeek',
    pointerleave: 'wait',
    dropdownOpen: 'blocked',
    click: 'unpeek',
    open: 'opened',
  },
  blocked: {
    dropdownClose: 'peek',
    open: 'opened',
  },
  wait: {
    toggle: 'opened',
    focusin: 'peek',
    pointerenter: 'peek',
    timeout: 'unpeek',
  },
  unpeek: {
    toggle: 'opened',
    focusin: 'peek',
    pointerenter: 'peek',
    transitionend: 'closed',
  },
})

type NavState = States<typeof navMachine>
type NavEvent = Events<typeof navMachine>

const isNavItem = (el: HTMLElement): el is NavItem => el.localName === 'nord-nav-item'
const isNavItemLeaf = (el: HTMLElement) => isNavItem(el) && !el.hasSubNav

/**
 * Layout component is used to create the main layout of an app. Layout
 * currently comes with one main configuration: two-column.
 *
 * @status ready
 * @category structure
 * @slot - The default main section content.
 * @slot nav - Used to place content inside the navigation sidebar.
 * @slot top-bar - Used to place the [Top Bar](../top-bar/) component.
 * @slot header - Used to place content inside the header section. This slot can be made sticky by utilizing the `sticky` property on the layout component.
 * @slot footer - Used to place content inside the footer section. This slot can be made sticky by utilizing the `stickyFooter` property on the layout component.
 * @slot drawer - Used to place additional content/details relating to a selected item.
 * @slot nav-toggle - Used to place a own nav-toggle component, for cases where you might need to add a tooltip.
 *
 * @cssprop [--n-layout-padding=var(--n-space-l)] - Controls the padding around the main layout area (the main slot), using our [spacing tokens](/tokens/#space).
 * @cssprop [--n-layout-drawer-inline-size=320px] - Controls the width of the drawer area, when used.
 * @cssprop [--n-layout-background-color=var(--n-color-background)] - Controls the background color of the layout, using [color tokens](/tokens/#color).
 */
@customElement('nord-layout')
export default class Layout extends LitElement {
  static styles = [componentStyle, stickyStyle, style]

  private peekTimeoutId?: ReturnType<typeof setTimeout>

  private navSlot = new SlotController(this, 'nav')
  private drawerSlot = new SlotController(this, 'drawer')
  private topBarSlot = new SlotController(this, 'top-bar')
  private headerSlot = new SlotController(this, 'header')
  private footerSlot = new SlotController(this, 'footer')
  private direction = new DirectionController(this)
  private events = new EventController(this)
  private lightDismiss = new LightDismissController(this, {
    isOpen: () => this.navState === 'opened' && !this.wideScreen,
    onDismiss: () => this.navTransition('close'),
    isDismissible: node => node !== this.navEl,
  })

  private broadcast = new BroadcastChannelController<{ navOpen: boolean } | { navWidth: number }>(this, {
    channelName: 'nord-layout-nav-state',
    enabled: () => this.syncNavState && this.wideScreen,
    onMessage: (data) => {
      if ('navOpen' in data) {
        this.navOpen = data.navOpen
      }
      if ('navWidth' in data) {
        this.navWidth = data.navWidth
      }
    },
  })

  @query('.n-layout-nav', true) private navEl!: HTMLDivElement

  @state() private navWidth = navWidthStore.value
  @state() private isDragging = false
  @state() private navState!: NavState
  @state() private wideScreen = mediaQuery.matches

  /**
   * Controls whether the navigation is hidden off-screen or not.
   * Defaults to `true` for wide viewports, and `false` otherwise.
   */
  @property({ reflect: true, type: Boolean, attribute: 'nav-open' }) navOpen!: boolean

  /**
   * Controls whether the navigation's open/closed state is persisted across page loads.
   * This is useful for multi-page apps, where clicks on links trigger a full page load.
   */
  @property({ reflect: true, type: Boolean, attribute: 'persist-nav-state' }) persistNavState = false

  /**
   * Controls whether the navigation's open/closed state and width is synced across tabs/windows on the same origin.
   * This is useful for long-lived app sessions where you may have multiple tabs/windows open at once.
   */
  @property({ type: Boolean, reflect: true, attribute: 'sync-nav-state' }) syncNavState = false

  /**
   * Controls the padding of the default main section slot. When set to “none”,
   * the nav and header slots will still have padding.
   */
  @property({ reflect: true }) padding: 'm' | 'none' = 'm'

  /**
   * Controls whether the layout's header has sticky positioning.
   */
  @property({ type: Boolean, reflect: true }) sticky: boolean = false

  /**
   * Controls whether the layout's footer has sticky positioning.
   */
  @property({ type: Boolean, reflect: true, attribute: 'sticky-footer' }) stickyFooter: boolean = false

  /**
   * A getter whose values reflects whether the layout component considers the viewport to be narrow or not.
   * A narrow viewport is considered to be less than 768px wide.
   */
  get isNarrow(): boolean {
    return !this.wideScreen
  }

  connectedCallback(): void {
    super.connectedCallback()
    this.events.listen(mediaQuery, 'change', this.handleMediaQueryChange)
  }

  willUpdate() {
    // we only want this logic to run once, before first render
    if (this.hasUpdated) {
      return
    }

    // on mobile it should always start closed
    if (this.isNarrow) {
      this.navState = 'closed'
    }
    // if nav state is persisted, and it was previously closed, start closed
    else if (this.persistNavState && navOpenStore.value === false) {
      this.navState = 'closed'
    }
    // otherwise start open
    else {
      this.navState = 'opened'
    }
  }

  render() {
    const { navWidth, navState, navSlot, headerSlot, footerSlot, topBarSlot, drawerSlot, isDragging, direction } = this
    const adjustedNavWidth = navState === 'opened' && this.wideScreen ? navWidth : NAV_DEFAULT_WIDTH

    return html`
      <div
        class=${classMap({
          'n-layout': true,
          'n-rtl': direction.isRTL,
          'n-dragging': isDragging,
          'n-has-header': headerSlot.hasContent,
          'n-has-footer': footerSlot.hasContent,
          'n-has-top-bar': topBarSlot.hasContent,
          'n-has-drawer': drawerSlot.hasContent,
          'n-has-nav': navSlot.hasContent,
        })}
        style=${styleMap({
          '--_n-layout-nav-width': `${adjustedNavWidth}px`,
        })}
        data-nav=${navSlot.hasContent ? navState : 'closed'}
        data-screen=${this.wideScreen ? 'wide' : 'narrow'}
      >
        <div
          class="n-layout-nav"
          ?hidden=${navSlot.isEmpty}
          @focusin=${this.handleNavFocus}
          @mouseenter=${this.handleMouseEnter}
          @mouseleave=${this.handleMouseLeave}
          @open=${this.handleDropdownOpen}
          @close=${this.handleDropdownClose}
        >
          <slot name="nav" @click=${this.handleNavClick}></slot>
          <div
            class="n-resize"
            role="separator"
            aria-orientation="vertical"
            tabindex="0"
            @pointerdown=${cond(navState === 'opened', this.startDragging)}
            @pointermove=${cond(isDragging, this.handleDrag)}
            @pointerleave=${this.stopDragging}
            @pointerup=${this.stopDragging}
            @keydown=${this.handleKeyboardResize}
            ?hidden=${this.isNarrow}
          ></div>
          ${this.renderNavCollapse()}
        </div>

        <div class="n-layout-main" @focusin=${this.handleMainFocus} @click=${this.handleMainClick}>
          ${navSlot.hasContent ? this.renderNavToggle() : nothing}

          <div class="n-top-bar-container">
            <slot name="top-bar"></slot>
          </div>

          <div class="n-layout-content">
            <div class="n-sticky">
              <slot name="header"></slot>
            </div>
            <main>
              <slot></slot>
            </main>
            <slot name="footer"></slot>
          </div>

          <aside ?hidden=${this.drawerSlot.isEmpty}>
            <slot name="drawer"></slot>
          </aside>
        </div>
      </div>
    `
  }

  private renderNavToggle() {
    return html`
      <div class="n-nav-toggle-container">
        <slot
          name="nav-toggle"
          @click=${this.handleToggleClick}
          @mouseover=${this.handleMouseEnter}
          @mouseout=${this.handleMouseLeave}
        >
          <nord-nav-toggle></nord-nav-toggle>
        </slot>
      </div>
    `
  }

  private renderNavCollapse() {
    return html`
      <button class="n-collapse-btn" @click=${this.handleToggleClick}>
        <nord-icon name="navigation-toggle-lock" size="s"></nord-icon>
      </button>
    `
  }

  @observe('navWidth', 'updated')
  protected handleNavWidthChange() {
    navWidthStore.value = this.navWidth
  }

  @observe('navState')
  protected handleNavStateChange(prev: NavState) {
    if (prev === 'wait' && this.peekTimeoutId) {
      clearTimeout(this.peekTimeoutId)
    }
    if (prev === 'unpeek') {
      this.navEl.removeEventListener('transitionend', this.handleTransitionEnd)
    }

    switch (this.navState) {
      case 'closed':
        this.navOpen = false
        break
      case 'opened':
        this.navOpen = true
        break
      case 'wait':
        this.peekTimeoutId = setTimeout(() => this.navTransition('timeout'), NAV_PEEK_DELAY)
        break
      case 'unpeek':
        this.navEl.addEventListener('transitionend', this.handleTransitionEnd, { once: true })
        break
      default:
        break
    }
  }

  @observe('navOpen', 'updated')
  protected handleOpenChange(prev: boolean) {
    if (!this.isDragging) {
      // when opening nav, it should restore to default width (or larger).
      // unless it is being dragged, in which case the drag width wins
      this.setNavWidth(Math.max(this.navWidth, NAV_DEFAULT_WIDTH))
    }

    this.navTransition(this.navOpen ? 'open' : 'close')

    if (this.persistNavState) {
      navOpenStore.value = this.navOpen
    }

    // skip first update, we only want subsequent changes
    if (prev != null) {
      this.broadcast.post({ navOpen: this.navOpen })
    }
  }

  /* ---------------------------------------------
  / NAVIGATION OPEN/CLOSE LOGIC
  / --------------------------------------------- */

  private navTransition(event: NavEvent) {
    this.navState = navMachine.transition(this.navState, event)
  }

  private handleNavClick(e: Event) {
    if (!e.defaultPrevented && !this.wideScreen) {
      const target = e.target as HTMLElement

      if (isNavItemLeaf(target)) {
        this.navTransition('close')
      }
    }
  }

  private handleMainClick() {
    this.navTransition('click')
  }

  private handleDropdownOpen(e: Event) {
    const target = e.target as Element

    if (target.localName === 'nord-dropdown') {
      this.navTransition('dropdownOpen')
    }
  }

  private handleDropdownClose(e: Event) {
    const target = e.target as Element

    if (target.localName === 'nord-dropdown') {
      this.navTransition('dropdownClose')
    }
  }

  private handleMediaQueryChange = () => {
    this.wideScreen = mediaQuery.matches
    this.navTransition(this.wideScreen ? 'open' : 'close')
  }

  private handleToggleClick = () => {
    this.navTransition('toggle')
  }

  private handleNavFocus = () => {
    this.navTransition('focusin')
  }

  private handleMainFocus = () => {
    this.navTransition('focusout')
  }

  private handleMouseEnter = () => {
    if (this.wideScreen) {
      this.navTransition('pointerenter')
    }
  }

  private handleMouseLeave = () => {
    this.navTransition('pointerleave')
  }

  private handleTransitionEnd = () => {
    this.navTransition('transitionend')
  }

  /* ---------------------------------------------
  / RESIZE LOGIC
  / --------------------------------------------- */

  private handleKeyboardResize(e: KeyboardEvent) {
    const {
      navWidth,
      direction: { isLTR },
    } = this

    switch (e.key) {
      case 'ArrowLeft':
        this.setNavWidth(navWidth + (isLTR ? -NAV_RESIZE_STEP : NAV_RESIZE_STEP))
        break
      case 'ArrowRight':
        this.setNavWidth(navWidth + (isLTR ? NAV_RESIZE_STEP : -NAV_RESIZE_STEP))
        break
      case 'Enter':
        this.navTransition('toggle')
        break
      case 'Home':
        this.setNavWidth(NAV_MIN_WIDTH)
        break
      case 'End':
        this.setNavWidth(NAV_MAX_WIDTH)
        break
      default:
        return
    }

    this.broadcast.post({ navWidth: this.navWidth })
    e.preventDefault()
  }

  private setNavWidth(width: number) {
    this.navWidth = clamp(Math.round(width), NAV_MIN_WIDTH, NAV_MAX_WIDTH)
  }

  private startDragging(e: PointerEvent) {
    if (e.button === 0) {
      const target = e.target as Element
      target.setPointerCapture(e.pointerId)
      this.isDragging = true
    }
  }

  private stopDragging() {
    this.isDragging = false
    this.broadcast.post({ navWidth: this.navWidth })
  }

  private handleDrag(e: PointerEvent) {
    const width = this.direction.isRTL ? this.clientWidth - e.clientX : e.clientX

    this.setNavWidth(width)
    this.navTransition(width >= NAV_COLLAPSE_WIDTH ? 'open' : 'close')
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-layout': Layout
  }
}
